// packages/auth/src/usePermissions.ts

import { useCallback } from 'react';

import type { SchemaType } from '@alamo/types';

import { useAuth } from './useAuth';

/**
 * Hook for permission checks in components.
 * Returns helper functions instead of raw permission arrays.
 *
 * @example
 * const { hasPermission, hasAnyPermission, hasRole, isMlmEligible } = usePermissions();
 *
 * if (hasPermission('quotation', 'create')) {
 *   // show create button
 * }
 *
 * if (isMlmEligible) {
 *   // show team / commissions / ranking
 * }
 */
export function usePermissions() {
  const { permissions, roleName, schemaType, consultantType } = useAuth();

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      return (
        permissions.includes(`${resource}:${action}`) || permissions.includes(`${resource}:manage`)
      );
    },
    [permissions],
  );

  /**
   * Exact match — does NOT expand `<resource>:manage` as a wildcard.
   *
   * Mandatory for every SCOPE permission (`*:read_all`, `*:write_all`,
   * `*:upload_document_all`, `payment:initiate_all`, `personal_data:*`).
   * `portfolio:manage` is granted to every external consultant, so checking
   * `portfolio:read_all` through `hasPermission` would hand the whole
   * portfolio scope to them.
   */
  const hasExactPermission = useCallback(
    (resource: string, action: string): boolean => permissions.includes(`${resource}:${action}`),
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (checks: Array<{ resource: string; action: string }>): boolean => {
      return checks.some(({ resource, action }) => hasPermission(resource, action));
    },
    [hasPermission],
  );

  // CLT consultors do not participate in the MLM hierarchy (no parent, no
  // subordinates, no commission split). Guard MLM-only UI with this flag.
  const isMlmEligible = consultantType === 'pf' || consultantType === 'pj';

  // True when the user owns a consultant_profile (any type). The consultant
  // surfaces (/team, /agenda, /commissions, /referrals) call endpoints scoped
  // by consultant_profile — without one they render empty or 403.
  const hasConsultantProfile = consultantType != null;

  return {
    hasPermission,
    hasExactPermission,
    hasAnyPermission,
    userRole: roleName,
    userPortal: schemaType as SchemaType,
    consultantType,
    hasConsultantProfile,
    isMlmEligible,
    permissions,
  };
}
